import { Button } from '@ophelos/ui/components/button';
import { Loader } from '@ophelos/ui/components/loader';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { ArrowRight, Fingerprint, Lock } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MarketFlag, MarketSwitcher } from '@/components/auth/MarketSwitcher';
import { GradientWaves, shade } from '@/components/backgrounds/GradientWaves';
import { OphelosIcon } from '@/components/branding/ophelos-icon';
import { getMarketHubs } from '@/lib/market-hubs';
import { getMarketOrganizationId } from '@/lib/market-organization';

const DEFAULT_BRAND = '#8750e5';

/**
 * Wave palette derived from the market's brand colour so the login matches per-market theming.
 * The shader fades distant water toward the horizon colour and then to transparent, so on a dark
 * page the horizon must be the light shade or the banding between swells disappears.
 */
function brandWavePalette() {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--brand-base').trim();
  const brand = /^#[a-f\d]{6}$/i.test(raw) ? raw : DEFAULT_BRAND;
  return { horizonColor: shade(brand, '#ffffff', 0.6), waveColor: brand, crestColor: shade(brand, '#000000', 0.45) };
}

export function LoginPage() {
  const { isAuthenticated, isLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const markets = getMarketHubs();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/apps');
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-soft">
        <Loader text="Loading..." />
      </div>
    );
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-brand-800">
      <GradientWaves
        className="absolute inset-0 overflow-hidden"
        {...brandWavePalette()}
        speed={0.2}
        waveRatio={0.45}
        tilt={1.3}
        fogDepth={13}
        amplitude={2.2}
        swell={40}
        zoom={1.2}
        turbulence={0}
        height={8.5}
        brightness={1.4}
        grain={false}
        mouseInteraction={false}
      />
      <div className="relative z-10 w-full max-w-3xl mx-6 p-6 sm:p-10 ">
        <div className="flex flex-col items-center justify-center text-center">
          {/* Icon */}
          <div className="mb-8 md:mb-10">
            <OphelosIcon className="size-12 md:size-20 text-white/50" />
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-semibold text-white mb-2">Welcome to the Ophelos Hub</h1>

          {/* Subtitle */}
          <p className="text-white/75 mb-10 text-base md:text-xl">Log in to your account</p>

          {/* Login button */}
          <Button
            size="xl"
            onClick={(e) => {
              e.preventDefault();
              // Pin to this market's WorkOS org — without the hint AuthKit mints the session against the user's default org, which may be another market's.
              // Post-login alignment is handled by MarketOrganizationGuard on protected routes.
              // Global surfaces ignore a baked org pin so AuthKit can show the org picker.
              const organizationId = getMarketOrganizationId();
              signIn(organizationId ? { organizationId } : undefined);
            }}
            variant="outline"
            className="flex-col xs:flex-row rounded-4xl xs:rounded-full group h-auto max-w-full py-4 xs:py-2 xs:pl-4! xs:pr-6! gap-4 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50! bg-white/15 hover:bg-white/30 backdrop-blur-2xl border-white/25 text-white"
          >
            {markets ? (
              <MarketFlag market={markets.current} className="size-8 shrink-0 rounded-full opacity-100! ring-2 ring-white" />
            ) : (
              <Lock className="size-4 opacity-75! ml-2" />
            )}
            {markets ? (
              <span className="flex min-w-0 flex-1 flex-col leading-tight max-xs:text-center">
                <span className="truncate text-lg font-semibold">{markets.current.name}</span>
                <span className=" truncate text-sm font-normal text-white/75">Log in securely</span>
              </span>
            ) : (
              <span className="truncate text-lg font-semibold">Log in securely</span>
            )}
            <ArrowRight className="max-xs:hidden opacity-100! ml-6 size-6 group-hover:translate-x-1 transition-transform duration-300" />
            <Fingerprint className="xs:hidden opacity-75! size-6" />
          </Button>

          {markets && (
            <MarketSwitcher
              markets={markets}
              className="mt-4 rounded-full text-white/75 hover:text-white hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50!"
            />
          )}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-6 z-10 flex items-center justify-center text-sm text-white/70">
        <Button
          variant="link"
          asChild
          className="text-white! hover:text-white! focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50!"
        >
          <Link to="/cookie-policy">Cookie policy</Link>
        </Button>
      </div>
    </div>
  );
}
